// The FIN payer route tree (/account/payments) — the student portal's second
// package-composed subtree, mirroring routes/opac.tsx: code-based routes
// (DEV-GUIDE-002 §4), a self-healing hand-written validateSearch (zod is not a
// direct portal dep), loaders that warm the SAME queryOptions the screens
// re-read, and screens closing their own `.lazy()` loop with createLazyRoute.
//
// GATING — the lib account-area pattern, on the FIN grant: this subtree is
// payer-only. PaymentsLayout re-checks fin:pay via ConsoleLayout (anonymous →
// /login; signed-in without the grant → an explicit denied panel, never a
// blank Outlet). The UI can only HIDE — the API's identity scoping
// (invoice.payer_user_id = session, FIN_NOT_PAYER 403) is the authority
// (ADR-001 §14.2). Every loader WARMS its cache entry with `void
// prefetchQuery(...)` and returns at once (audit UX-01, rationale on the admin
// index route): a member read runs before the layout gate has bounced an
// anonymous visitor, and the loader must NOT fail or hold the route — the
// screen's own useQuery observes the error and renders the redirect/denied
// state, and paints its own bones while the read is on the wire.
import { Outlet, createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import { finQueries } from "../index.js";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { panelSearch, type PanelSearch } from "@erp/ui/side-panel";
import type { AnyRoute } from "@tanstack/solid-router";

/** Keyset page size — the SAME constant the loader and the screens use, so both hit one cache entry. */
export const PAYMENTS_PAGE_SIZE = 20;

/**
 * /account/payments carries NO cursor (2026-09-22). Both lists STREAM: each is
 * one `useInfiniteQuery` entry whose pages accumulate, so a cursor is a
 * position inside a stream rather than a view of it, and the two it used to
 * carry (`invCursor` / `payCursor`) have left the URL, the deps and the loader
 * alike. A stale one on an old bookmark falls out of this validator and reaches
 * neither stream — the payer's home opens at the newest invoice and the newest
 * claim, with the claim panel restored.
 *
 * The NAME survives — as an alias now, because with both cursors gone the
 * claim VIEWER's panel params are all this route's search state is, and an
 * interface that declares no members of its own is just its supertype under
 * another spelling.
 */
export type MyPaymentsSearch = PanelSearch;

/**
 * The closed panel set for the payer's home. A claim's outcome (the receipt
 * number, or the checker's rejection reason) is truncated in the table, so the
 * full record opens in a URL side panel — `?panel=claim&panelId=<paymentId>` —
 * which a payer can bookmark or send to the office. Anything else heals away.
 */
export const MY_PAYMENTS_PANELS = ["claim"] as const;

export function validateMyPaymentsSearch(input: Record<string, unknown>): MyPaymentsSearch {
  return panelSearch(MY_PAYMENTS_PANELS, input);
}

/**
 * The payer shell: gates on a live session + fin:pay, then renders the active
 * child. Same ConsoleLayout the lib /account subtree uses, so "signed out"
 * and "signed in without the grant" behave identically across both member
 * surfaces of this portal.
 */
function PaymentsLayout() {
  return (
    <ConsoleLayout
      perm="fin:pay"
      deniedMessage="This account cannot make payments. Ask an administrator for payments access."
      skeletonId="payments-skeleton"
      deniedId="payments-denied"
      unreachableId="payments-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/**
 * Build the payer subtree under the portal's root route. Returns the routes to
 * spread into `rootRoute.addChildren([...])` (router.tsx) beside the OPAC
 * trees. "account/payments" is a static sibling of the lib "account" route —
 * the two member surfaces gate on DIFFERENT grants (lib:opac vs fin:pay), so
 * they deliberately do not share a layout.
 */
export function makePaymentsRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const paymentsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "account/payments",
    component: PaymentsLayout,
  });

  const paymentsIndexRoute = createRoute({
    getParentRoute: () => paymentsRoute,
    path: "/",
    validateSearch: validateMyPaymentsSearch,
    // Warm the SAME cache entries the screen re-keys — WITHOUT holding the route
    // (audit UX-01; the rationale is on the admin index route).
    // prefetchInfiniteQuery never throws, so the old `.catch` guards are not
    // needed. The FIRST PAGE of each stream only; every page after it is
    // fetched by that list's own sentinel. No loaderDeps: nothing in the URL
    // narrows either stream any more, so both warm the one entry they have.
    loader: ({ context }) => {
      void context.queryClient.prefetchInfiniteQuery(finQueries.myInvoicesInfinite({ limit: PAYMENTS_PAGE_SIZE }));
      void context.queryClient.prefetchInfiniteQuery(finQueries.myPaymentsInfinite({ limit: PAYMENTS_PAGE_SIZE }));
    },
  }).lazy(lazyRoute(() => import("../screens/student/MyPayments.js")));

  const invoicePayRoute = createRoute({
    getParentRoute: () => paymentsRoute,
    path: "$invoiceId",
    loader: ({ context, params }) => {
      void context.queryClient.prefetchQuery(finQueries.myInvoice(params.invoiceId));
    },
  }).lazy(lazyRoute(() => import("../screens/student/InvoicePay.js")));

  return [paymentsRoute.addChildren([paymentsIndexRoute, invoicePayRoute])];
}
